import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  Divider,
  Grid,
  Autocomplete,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  InputAdornment,
} from '@mui/material';
import AppTheme from '../../../../shared-theme/AppTheme';
import { CssBaseline } from '@mui/material';
import SideMenu from '../../../../components/SideMenu';
import Header from '../../../../components/Header';
import { ArrowBack, Print, PrintOutlined, RemoveCircleOutline, RemoveRedEyeOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Selector from '../../../../components/Selector';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';

export default function PagoDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [pago, setPago] = useState(null);
  const [factura, setFactura] = useState(null);
  const [proveedor, setProveedor] = useState(null);
  const [moneda, setMoneda] = useState(null);
  const [banco, setBanco] = useState(null);
  const [notas, setNotas] = useState("");

  useEffect(() => {
    // Fetch pago details
    async function fetchPago() {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/pagos-compra/${localStorage.getItem('idcompany')}`);
      const pagos = await response.json();
      const pagoEncontrado = pagos.find(p => p.id === id);
      setPago(pagoEncontrado);

      // Fetch factura details
      if (pagoEncontrado) {
        const responseFactura = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/facturas-compra/${localStorage.getItem('idcompany')}`);
        const facturas = await responseFactura.json();
        const facturaEncontrada = facturas.find(f => f.id === pagoEncontrado.id_factura_compra);
        setFactura(facturaEncontrada);

        // Set proveedor, moneda, banco
        if (facturaEncontrada) {
          setProveedor({
            label: facturaEncontrada.proveedor_name,
            value: facturaEncontrada.id_proveedor,
            rnc: facturaEncontrada.rnc,
          });
          setMoneda({
            label: facturaEncontrada.simbolo_moneda,
            value: facturaEncontrada.moneda,
            symbol: facturaEncontrada.symbol || "",
          });
        }
        setBanco({
          label: pagoEncontrado.banco_name,
          value: pagoEncontrado.id_banco,
        });
        setNotas(pagoEncontrado.referencia_pago);
      }
    }
    fetchPago();
  }, [id]);

  if (!pago || !factura) {
    return (
      <Box sx={{ width: "100%", minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6">Cargando detalles del pago...</Typography>
      </Box>
    );
  }

  return (
    <AppTheme themeComponents={{}}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}>
            <Header parent={"Logistica y Almacenes"} child={"Pagos"} />
            <Container maxWidth={"lg"} sx={{ px: { xs: 2, md: 10 }, py: 5, display: "flex", justifyContent: "center", flexDirection: "column", backgroundColor: "transparent"}}>
              <Box dividers display={"flex"} justifyContent={"flex-start"} >
                <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>{navigate(-1)}}>Atras</Button>
                <Box display={"flex"} flexDirection={"column"} gap={2} ml={5}>
                  <Typography variant="h4" fontWeight={700} textAlign={"left"}>Detalle de Pago</Typography>
                  <Typography variant="body1" color="text.secondary" textAlign={"left"} sx={{mt:-1}}>Visualiza la información del pago realizado a una factura de proveedor</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                  <Box>
                    <Button variant="outlined" color="error" sx={{ fontWeight: "bold" , borderColor:"error.main", color:"error.main"}} startIcon={<RemoveCircleOutline/>}>
                      Eliminar Pago
                    </Button>
                  </Box>
                </Box>

              <Box boxShadow={1} p={2} borderRadius={3} mt={2} padding={5}>
                <Grid container spacing={2}>
                  <Grid item size={12}>
                    <Typography variant="h6" fontWeight={700} textAlign={"left"}>Información General del Pago</Typography>
                  </Grid>
                  <Grid item size={4}>
                    <TextField
                      label="Proveedor"
                      value={proveedor ? proveedor.label : ""}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item size={4}>
                    <TextField
                      label="Cuenta Bancaria"
                      value={banco ? banco.label : ""}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item size={4}>
                    <TextField
                      label="Fecha de Pago"
                      value={pago.fecha_pago}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item size={4}>
                    <TextField
                      label="Método de Pago"
                      value={pago.metodo_pago}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item size={4}>
                    <TextField
                      label="Moneda"
                      value={moneda ? moneda.label : ""}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item size={8}>
                    {notas && (
                        <Box sx={{display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 2}}>
                            <Typography variant="body1" color="text.secondary" textAlign={"left"} fontWeight={700}>Notas:</Typography>
                            <Typography variant="body1" color="text.secondary" textAlign={"left"}>{notas}</Typography>
                        </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Box boxShadow={1} p={2} borderRadius={3} mt={2} padding={5}>
                <Grid container spacing={2}>
                  <Grid item size={12}>
                    <Typography variant="h6" fontWeight={700} textAlign={"left"}>Factura Pagada</Typography>
                    <Typography variant="body1" color="text.secondary" textAlign={"left"}>Detalle de la factura asociada a este pago</Typography>
                  </Grid>
                  <Grid item size={12}>
                    <TableContainer component={Paper} sx={{ boxShadow: "none", minHeight: "100px" }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <StyledCellHeader>Numero</StyledCellHeader>
                            <StyledCellHeader>Vencimiento</StyledCellHeader>
                            <StyledCellHeader>Total</StyledCellHeader>
                            <StyledCellHeader>Pagado</StyledCellHeader>
                            <StyledCellHeader>Por Pagar</StyledCellHeader>
                            <StyledCellHeader>Monto Pagado</StyledCellHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{factura.numero_factura}</TableCell>
                            <TableCell>{factura.fecha_vencimiento}</TableCell>
                            <TableCell>{factura.monto_total}</TableCell>
                            <TableCell>{factura.monto_pagado}</TableCell>
                            <TableCell>
                              {parseFloat(factura.monto_total) - parseFloat(factura.monto_pagado)}
                            </TableCell>
                            <TableCell>
                              {pago.monto_pagado}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item size={12}>
                    <Divider sx={{mb:2}}></Divider>
                    <Box sx={{ display: "flex", justifyContent: "end", mt: 2, mr: 2 }}>
                      <Typography variant="h6" color="text.secondary" textAlign={"left"} fontWeight={700}>
                        Total Pagado: {pago.monto_pagado}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
